import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { api, type Me } from './api';

type Session = {
  user: Me | null;
  loading: boolean;
  signIn: (login: string, password: string) => Promise<Me | null>;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
};

const Ctx = createContext<Session | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await api.get<{ user: Me | null }>('/auth/me');
      setUser(res.user);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const value = useMemo<Session>(() => ({
    user, loading, refresh,
    signIn: async (login, password) => {
      await api.post('/auth/login', { login, password });
      const res = await api.get<{ user: Me | null }>('/auth/me');
      setUser(res.user);
      return res.user;
    },
    signOut: async () => { await api.post('/auth/logout'); setUser(null); },
  }), [user, loading, refresh]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useSession must be used inside SessionProvider');
  return ctx;
}
